import * as React from 'react';
import { useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, ClipboardCheck } from 'lucide-react';
import { ComplianceCheck } from '../utils/compliance';

interface PlanReviewProps {
    checks: ComplianceCheck[];
}

const PlanReview: React.FC<PlanReviewProps> = ({ checks }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isValid = checks.every(c => c.pass);
    const passCount = checks.filter(c => c.pass).length;

    return (
        <div className="flex flex-col gap-2 pointer-events-auto min-w-[140px]">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl border backdrop-blur-md shadow-xl transition-all active:scale-95
                ${isValid
                        ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                        : 'bg-amber-500/10 border-amber-500/20 text-amber-400'}`}
            >
                <div className="flex items-center gap-2">
                    {isValid ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                    <div className="flex flex-col items-start translate-y-[1px]">
                        <span className="text-[10px] font-black uppercase tracking-widest leading-none">
                            Plan Review
                        </span>
                        <span className={`text-[9px] font-bold opacity-80 leading-tight`}>
                            {isValid ? 'Ready to Export' : `${checks.length - passCount} Items Pending`}
                        </span>
                    </div>
                </div>
                {isOpen ? <ChevronUp size={16} className="opacity-50" /> : <ChevronDown size={16} className="opacity-50" />}
            </button>

            {isOpen && (
                <div className="w-full p-4 bg-[var(--color-primary-black)]/90 backdrop-blur-xl border border-[var(--color-foreground)]/10 rounded-2xl shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center gap-2 mb-3 border-b border-[var(--color-foreground)]/5 pb-2">
                        <ClipboardCheck size={14} className="text-[var(--color-primary-light-gray)]" />
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-[var(--color-primary-light-gray)]">
                            Checklist
                        </h4>
                    </div>

                    <ul className="space-y-3">
                        {checks.map((check, i) => (
                            <li key={i} className="flex items-start gap-3 group">
                                <div className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center border transition-colors
                                    ${check.pass 
                                        ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' 
                                        : 'bg-[var(--color-primary-dark-gray)] border-[var(--color-foreground)]/10 text-[var(--color-primary-gray)]'}`}>
                                    {check.pass && <CheckCircle2 size={10} />}
                                </div>
                                <span className={`text-xs transition-colors ${check.pass ? 'text-emerald-500/70' : 'text-[var(--color-primary-light-gray)]'}`}>
                                    {check.label}
                                </span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-4 pt-2 border-t border-[var(--color-foreground)]/5 text-[8px] text-[var(--color-primary-gray)] font-bold uppercase tracking-tighter opacity-40 italic">
                        Technical Pre-Flight Review
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanReview;
